import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  HelpCircle, X, ArrowRight, ArrowLeft, BookOpen, Compass,
  // Icons that may be referenced from helpContent.js — make sure they're
  // imported here so the dynamic lookup below resolves them.
  CalendarDays, Bus, Link as LinkIcon, Filter, RefreshCw, Sparkles,
  MousePointer2, Radio, AlertCircle, MapPin, ClipboardList, FileText,
  Download, Phone, FileSpreadsheet, Upload, CheckCircle2, UserCog,
  ShieldCheck, ShieldAlert,
} from "lucide-react";
import { getHelpForPath } from "@/help/helpContent";

/** Icon registry — lets helpContent.js reference Lucide icons by string. */
const ICONS = {
  CalendarDays, Bus, LinkIcon, Filter, RefreshCw, Sparkles, MousePointer2,
  Radio, AlertCircle, MapPin, ClipboardList, FileText, Download, Phone,
  FileSpreadsheet, Upload, CheckCircle2, UserCog, ShieldCheck, ShieldAlert,
  HelpCircle, ArrowRight,
};
const Icon = ({ name, ...rest }) => {
  const C = ICONS[name] || HelpCircle;
  return <C {...rest} />;
};

/**
 * Floating "?" button shown in the bottom-right corner of admin pages.
 * Opens a chooser → either:
 *   - Aide rapide (side drawer with cards)
 *   - Visite guidée (modal slides)
 *
 * Content is sourced from /help/helpContent.js based on the current route.
 * If no content exists for the route, the button hides itself.
 */
export default function HelpButton({ offsetBottom = "bottom-5" }) {
  const { pathname } = useLocation();
  const help = useMemo(() => getHelpForPath(pathname), [pathname]);
  const [mode, setMode] = useState(null); // null | "menu" | "drawer" | "tour"
  const [slide, setSlide] = useState(0);

  // Reset slide when the help content changes (route change)
  useEffect(() => { setSlide(0); setMode(null); }, [pathname]);

  // Close on Escape
  useEffect(() => {
    if (!mode) return;
    const onKey = (e) => {
      if (e.key === "Escape") setMode(null);
      if (mode === "tour" && e.key === "ArrowRight") setSlide((s) => Math.min(s + 1, (help?.tour?.length || 1) - 1));
      if (mode === "tour" && e.key === "ArrowLeft")  setSlide((s) => Math.max(s - 1, 0));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mode, help]);

  if (!help) return null;

  return (
    <>
      {/* Floating launcher */}
      <button
        onClick={() => setMode("menu")}
        data-testid="help-fab"
        aria-label="Aide pour cette page"
        title="Aide pour cette page"
        className={`fixed ${offsetBottom} right-5 z-[900] w-12 h-12 bg-zinc-900 text-[#FFC60A] border-2 border-[#FFC60A] shadow-[3px_3px_0_0_#0A0A0B] flex items-center justify-center hover:bg-[#FFC60A] hover:text-zinc-900 transition-colors`}
      >
        <HelpCircle size={22} strokeWidth={2.5} />
      </button>

      {/* Mode chooser */}
      {mode === "menu" && (
        <div className="fixed inset-0 z-[1000] bg-black/40 flex items-center justify-center p-4" onClick={() => setMode(null)}>
          <div onClick={(e) => e.stopPropagation()} data-testid="help-menu"
            className="bg-white border-2 border-zinc-900 shadow-[6px_6px_0_0_#FFC60A] w-full max-w-md">
            <header className="px-4 py-3 bg-zinc-900 text-white flex items-center gap-3">
              <HelpCircle size={16} className="text-[#FFC60A]" />
              <div className="flex-1">
                <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#FFC60A]">AIDE · {help.title}</div>
                <div className="font-display font-black text-lg tracking-tighter">{help.tagline}</div>
              </div>
              <button onClick={() => setMode(null)} data-testid="help-menu-close" className="text-zinc-400 hover:text-white"><X size={18} /></button>
            </header>
            <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button onClick={() => setMode("drawer")} data-testid="help-open-drawer"
                className="border-2 border-zinc-300 hover:border-zinc-900 hover:bg-zinc-50 p-4 text-left transition-colors">
                <BookOpen size={22} className="text-[#FFC60A]" />
                <div className="mt-2 font-display font-black text-base tracking-tighter">Aide rapide</div>
                <div className="text-[11px] text-zinc-500 mt-1">Toutes les explications en cartes (drawer latéral).</div>
              </button>
              <button onClick={() => setMode("tour")} data-testid="help-open-tour"
                className="border-2 border-zinc-300 hover:border-zinc-900 hover:bg-zinc-50 p-4 text-left transition-colors">
                <Compass size={22} className="text-[#FFC60A]" />
                <div className="mt-2 font-display font-black text-base tracking-tighter">Visite guidée</div>
                <div className="text-[11px] text-zinc-500 mt-1">Étape par étape ({help.tour?.length || 0} slides).</div>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Drawer */}
      {mode === "drawer" && (
        <div className="fixed inset-0 z-[1000] flex" onClick={() => setMode(null)}>
          <div className="flex-1 bg-black/40" />
          <aside onClick={(e) => e.stopPropagation()} data-testid="help-drawer"
            className="w-full sm:w-[480px] bg-white border-l-2 border-zinc-900 shadow-[-6px_0_0_0_#FFC60A] flex flex-col">
            <header className="px-4 py-3 bg-zinc-900 text-white flex items-center gap-3 flex-shrink-0">
              <BookOpen size={16} className="text-[#FFC60A]" />
              <div className="flex-1 min-w-0">
                <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#FFC60A]">AIDE RAPIDE</div>
                <div className="font-display font-black text-lg tracking-tighter truncate">{help.title}</div>
              </div>
              <button onClick={() => setMode("tour")} data-testid="help-drawer-switch-tour"
                className="text-[10px] font-mono uppercase tracking-widest text-zinc-300 hover:text-[#FFC60A] inline-flex items-center gap-1">
                <Compass size={11} /> Visite
              </button>
              <button onClick={() => setMode(null)} data-testid="help-drawer-close" className="text-zinc-400 hover:text-white"><X size={18} /></button>
            </header>
            <div className="text-xs text-zinc-600 px-4 pt-3">{help.tagline}</div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {(help.quick || []).map((card, i) => (
                <article key={i} data-testid={`help-card-${i}`} className="border border-zinc-200 bg-zinc-50/50 p-3">
                  <div className="flex items-start gap-2">
                    <div className="w-8 h-8 bg-zinc-900 text-[#FFC60A] border border-zinc-900 flex items-center justify-center flex-shrink-0">
                      <Icon name={card.icon} size={15} strokeWidth={2.5} />
                    </div>
                    <div className="min-w-0">
                      <div className="font-display font-black text-sm tracking-tighter">{card.title}</div>
                      <p className="text-[12px] text-zinc-700 leading-relaxed mt-0.5">{card.body}</p>
                    </div>
                  </div>
                </article>
              ))}
              <div className="text-[10px] font-mono text-zinc-400 pt-2 border-t border-zinc-100">
                Astuce : appuyez sur Échap pour fermer.
              </div>
            </div>
          </aside>
        </div>
      )}

      {/* Tour modal (slides) */}
      {mode === "tour" && (help.tour?.length > 0) && (
        <div className="fixed inset-0 z-[1000] bg-black/60 flex items-center justify-center p-4" onClick={() => setMode(null)}>
          <div onClick={(e) => e.stopPropagation()} data-testid="help-tour"
            className="bg-white border-2 border-zinc-900 shadow-[6px_6px_0_0_#FFC60A] w-full max-w-2xl flex flex-col">
            <header className="px-4 py-3 bg-zinc-900 text-white flex items-center gap-3">
              <Compass size={16} className="text-[#FFC60A]" />
              <div className="flex-1 min-w-0">
                <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#FFC60A]">VISITE GUIDÉE · {help.title}</div>
                <div className="font-display font-black text-lg tracking-tighter truncate">Étape {slide + 1} / {help.tour.length}</div>
              </div>
              <button onClick={() => setMode("drawer")} data-testid="help-tour-switch-drawer"
                className="text-[10px] font-mono uppercase tracking-widest text-zinc-300 hover:text-[#FFC60A] inline-flex items-center gap-1">
                <BookOpen size={11} /> Aide rapide
              </button>
              <button onClick={() => setMode(null)} data-testid="help-tour-close" className="text-zinc-400 hover:text-white"><X size={18} /></button>
            </header>

            {/* Illustration zone — placeholder with the slide's icon */}
            <div className="px-4 pt-4">
              <div className="bg-[#FFC60A] border-2 border-zinc-900 shadow-[3px_3px_0_0_#0A0A0B] h-44 flex items-center justify-center" data-testid={`help-tour-illu-${slide}`}>
                <Icon name={help.tour[slide].icon} size={80} strokeWidth={1.8} className="text-zinc-900" />
              </div>
            </div>

            <div className="p-4">
              <div className="font-display font-black text-2xl tracking-tighter" data-testid="help-tour-title">{help.tour[slide].title}</div>
              <p className="text-sm text-zinc-700 mt-2 leading-relaxed" data-testid="help-tour-body">{help.tour[slide].body}</p>

              {/* Dots */}
              <div className="flex items-center gap-1.5 mt-4" data-testid="help-tour-dots">
                {help.tour.map((_, i) => (
                  <button key={i} onClick={() => setSlide(i)} data-testid={`help-tour-dot-${i}`}
                    className={`h-1.5 transition-all border border-zinc-900 ${i === slide ? "w-6 bg-zinc-900" : "w-3 bg-white hover:bg-zinc-200"}`} />
                ))}
              </div>
            </div>

            <footer className="px-4 py-3 border-t border-zinc-200 bg-zinc-50 flex items-center gap-2">
              <button onClick={() => setSlide((s) => Math.max(s - 1, 0))} disabled={slide === 0} data-testid="help-tour-prev"
                className="px-3 py-1.5 border border-zinc-300 hover:border-zinc-900 hover:bg-white text-[11px] font-bold uppercase tracking-widest inline-flex items-center gap-1 disabled:opacity-40">
                <ArrowLeft size={12} /> Précédent
              </button>
              <div className="flex-1" />
              {slide < help.tour.length - 1 ? (
                <button onClick={() => setSlide((s) => s + 1)} data-testid="help-tour-next"
                  className="px-4 py-1.5 bg-zinc-900 text-white border border-zinc-900 hover:bg-[#FFC60A] hover:text-zinc-900 text-[11px] font-black uppercase tracking-widest inline-flex items-center gap-1.5">
                  Suivant <ArrowRight size={12} />
                </button>
              ) : (
                <button onClick={() => setMode(null)} data-testid="help-tour-finish"
                  className="px-4 py-1.5 bg-[#FFC60A] text-zinc-900 border border-zinc-900 hover:bg-zinc-900 hover:text-[#FFC60A] text-[11px] font-black uppercase tracking-widest inline-flex items-center gap-1.5">
                  Terminer <CheckCircle2 size={12} />
                </button>
              )}
            </footer>
          </div>
        </div>
      )}
    </>
  );
}
